/**
 * Google Analytics Utility
 * 
 * Provides a centralized interface for Google Analytics tracking.
 * Supports both standard GA4 events and custom events.
 * 
 * Configuration:
 * - Update GA_MEASUREMENT_ID constant below with your GA4 Measurement ID
 * - Use a test ID (e.g., "G-TEST123456") for development
 * - Replace with production ID when ready
 */

// Google Analytics Measurement ID
// Replace with your actual GA4 Measurement ID when ready
const GA_MEASUREMENT_ID = 'G-R6ZXW1HT6R'

// TypeScript declarations for Google Analytics
declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
}

/**
 * Get the Google Analytics Measurement ID
 */
const getGAId = (): string => {
  return GA_MEASUREMENT_ID
}

/**
 * Check if Google Analytics is initialized
 */
const isGAInitialized = (): boolean => {
  return typeof window !== 'undefined' && 
         typeof window.gtag === 'function' &&
         Array.isArray(window.dataLayer)
}

// Track if GA has been disabled by user
let gaDisabled = false

/**
 * Check if GA is currently disabled
 */
export const isGADisabled = (): boolean => {
  return gaDisabled
}

/**
 * Re-enable Google Analytics (for users who previously rejected but now accept)
 */
export const reEnableGA = (): void => {
  gaDisabled = false
  initializeGA()
}

/**
 * Initialize Google Analytics
 * Loads the gtag.js script and configures it. Must only be called after the
 * user has given consent via the cookie banner (see Cookies.tsx) - this is
 * the sole place GA tracking gets turned on.
 */
export const initializeGA = (): void => {
  if (typeof window === 'undefined') return

  // Don't initialize if user has disabled tracking
  if (gaDisabled) {
    return
  }

  // Already loaded, don't inject the script tag twice
  if (isGAInitialized()) {
    return
  }

  const gaId = getGAId()

  window.dataLayer = window.dataLayer || []
  window.gtag = (...args: unknown[]) => {
    window.dataLayer.push(args)
  }
  window.gtag('js', new Date())
  window.gtag('config', gaId)

  const script = document.createElement('script')
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`
  script.async = true
  document.head.appendChild(script)
}

/**
 * Disable Google Analytics tracking
 * Removes GA scripts and clears dataLayer
 */
export const disableGA = (): void => {
  if (typeof window === 'undefined') return
  
  gaDisabled = true
  
  // Remove GA script tags
  const scripts = document.querySelectorAll('script[src*="googletagmanager.com/gtag/js"]')
  scripts.forEach(script => script.remove())
  
  // Clear dataLayer and remove gtag function
  if (window.dataLayer) {
    window.dataLayer = []
  }
  
  if (window.gtag) {
    window.gtag = () => {}
  }
  
  // console.log('[GA] Google Analytics disabled')
}

/**
 * Track a page view
 * 
 * @param path - The page path (optional, defaults to current path)
 * @param title - The page title (optional, defaults to document.title)
 */
export const trackPageView = (path?: string, title?: string): void => {
  if (gaDisabled || !isGAInitialized()) {
    // Silently fail if GA is disabled or not initialized
    return
  }

  const gaId = getGAId()
  window.gtag('config', gaId, {
    page_path: path || window.location.pathname,
    page_title: title || document.title,
    page_location: window.location.href
  })

  // console.log('[GA] Page view tracked:', { path: path || window.location.pathname, title: title || document.title })
}

/**
 * Track a standard GA4 event
 * 
 * @param eventName - The event name (e.g., 'click', 'download', 'share')
 * @param eventParams - Optional event parameters
 * 
 * @example
 * trackEvent('button_click', { button_name: 'Contact Us', button_location: 'header' })
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, unknown>
): void => {
  if (gaDisabled || !isGAInitialized()) {
    // Silently fail if GA is disabled or not initialized
    return
  }

  window.gtag('event', eventName, eventParams || {})
  // console.log('[GA] Event tracked:', eventName, eventParams)
}

/**
 * Track a custom event with additional context
 * 
 * @param category - Event category (e.g., 'User Interaction', 'Form', 'Navigation')
 * @param action - Event action (e.g., 'Click', 'Submit', 'View')
 * @param label - Optional label for additional context
 * @param value - Optional numeric value
 * 
 * @example
 * trackCustomEvent('Form', 'Submit', 'Contact Form', 1)
 */
export const trackCustomEvent = (
  category: string,
  action: string,
  label?: string,
  value?: number
): void => {
  if (gaDisabled || !isGAInitialized()) {
    // Silently fail if GA is disabled or not initialized
    return
  }

  const eventParams: Record<string, unknown> = {
    event_category: category,
    event_action: action
  }

  if (label) {
    eventParams.event_label = label
  }

  if (value !== undefined) {
    eventParams.value = value
  }

  window.gtag('event', action, eventParams)
  // console.log('[GA] Custom event tracked:', { category, action, label, value })
}

/**
 * Track form submissions
 * 
 * @param formName - Name/identifier of the form
 * @param formLocation - Where the form is located on the page
 * @param success - Whether the submission was successful
 */
export const trackFormSubmit = (
  formName: string,
  formLocation?: string,
  success: boolean = true
): void => {
  trackEvent('form_submit', {
    form_name: formName,
    form_location: formLocation,
    success
  })
}

/**
 * Track button clicks
 * 
 * @param buttonName - Name/identifier of the button
 * @param buttonLocation - Where the button is located
 * @param buttonType - Type of button (e.g., 'CTA', 'Link', 'Navigation')
 */
export const trackButtonClick = (
  buttonName: string,
  buttonLocation?: string,
  buttonType?: string
): void => {
  trackEvent('button_click', {
    button_name: buttonName,
    button_location: buttonLocation,
    button_type: buttonType
  })
}

/**
 * Track link clicks
 * 
 * @param linkText - The text of the link
 * @param linkUrl - The destination URL
 * @param linkLocation - Where the link is located
 */
export const trackLinkClick = (
  linkText: string,
  linkUrl: string,
  linkLocation?: string
): void => {
  trackEvent('link_click', {
    link_text: linkText,
    link_url: linkUrl,
    link_location: linkLocation
  })
}

/**
 * Track file downloads
 * 
 * @param fileName - Name of the downloaded file
 * @param fileType - Type/extension of the file
 * @param fileLocation - Where the download was initiated from
 */
export const trackDownload = (
  fileName: string,
  fileType?: string,
  fileLocation?: string
): void => {
  trackEvent('file_download', {
    file_name: fileName,
    file_type: fileType,
    file_location: fileLocation
  })
}

/**
 * Track video interactions
 * 
 * @param action - Video action (e.g., 'play', 'pause', 'complete')
 * @param videoTitle - Title of the video
 * @param videoLocation - Where the video is located
 */
export const trackVideo = (
  action: 'play' | 'pause' | 'complete' | 'progress',
  videoTitle: string,
  videoLocation?: string,
  progress?: number
): void => {
  const eventParams: Record<string, unknown> = {
    video_title: videoTitle,
    video_location: videoLocation
  }

  if (progress !== undefined) {
    eventParams.progress = progress
  }

  trackEvent(`video_${action}`, eventParams)
}

/**
 * Track scroll depth
 * 
 * @param depth - Scroll depth percentage (10, 20, 30, 40, 50, 60, 70, 80, 90, 100)
 * @param pagePath - Optional page path where the scroll occurred
 */
export const trackScrollDepth = (depth: 10 | 20 | 30 | 40 | 50 | 60 | 70 | 80 | 90 | 100, pagePath?: string): void => {
  const eventParams: Record<string, unknown> = {
    scroll_depth: depth
  };
  
  if (pagePath) {
    eventParams.page_path = pagePath;
  }
  
  trackEvent('scroll', eventParams)
}

/**
 * Track search queries
 * 
 * @param searchTerm - The search query
 * @param resultCount - Number of results (optional)
 */
export const trackSearch = (
  searchTerm: string,
  resultCount?: number
): void => {
  const eventParams: Record<string, unknown> = {
    search_term: searchTerm
  }

  if (resultCount !== undefined) {
    eventParams.result_count = resultCount
  }

  trackEvent('search', eventParams)
}

/**
 * Check if GA is available and initialized
 */
export const isGAReady = (): boolean => {
  return isGAInitialized()
}

/**
 * Get the current GA Measurement ID
 */
export const getGAMeasurementId = (): string => {
  return getGAId()
}

